'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA, BlogWhatTheAppChecks } from '@/app/components/blog/BlogAeo'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Microplastics in Sushi: Raw Fish, Soy Sauce Packets & Plastic Trays"
      author="MicroPlastics Editorial Team"
      date="August 30, 2025"
      readTime="8 min read"
      heroImage="/blog/app-tracking.png"
      heroImageAlt="Microplastics in sushi, raw fish, trays, soy sauce packets"
    >
      <BlogLastUpdated iso="2026-05-25" formatted="May 25, 2026" />

      <BlogTLDR>
        Sushi is a higher-than-average microplastic meal because of three
        compounding sources: (1) <strong>raw fish</strong> carries the highest
        microplastic load of any common protein (whole fish vs filleted matters);
        (2) the <strong>black plastic takeout tray</strong> contains brominated
        flame retardants from recycled e-waste; and (3) the <strong>fish-shaped
        soy sauce packets</strong> are PE-laminated and may shed particles
        directly into soy. Restaurant sushi served on ceramic plates with
        wasabi and pickled ginger in their own dishes is dramatically cleaner
        than supermarket / takeout sushi in plastic trays.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <>Raw fish has a higher microplastic load than cooked fish at the same volume, particles found in 100% of seafood samples tested.</>,
          <>Black plastic sushi trays (the standard supermarket/takeout format) are part of the recycled-e-waste flame retardant problem (Toxic-Free Future 2024).</>,
          <>Fish-shaped soy sauce packets (PE-laminated bioplastic or HDPE) sit in direct soy contact and may release plastic into the sauce.</>,
          <>Bluefin tuna, swordfish, and other large predator fish concentrate microplastic alongside mercury.</>,
          <>Restaurant sushi on ceramic, with whole-leaf wasabi from a separate dish, is the cleanest sushi option.</>,
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Why raw fish concentrates microplastic
      </h2>
      <p>
        Fish ingest microplastic through their gills and digestive systems.
        Particles concentrate in the gut, gills, and to a lesser extent in
        muscle tissue. When fish is cooked, the gut is removed first, but
        raw preparations like sushi often use whole-fish cuts that preserve
        more of the microplastic load. Several factors compound:
      </p>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>Large predator fish</strong> (bluefin tuna, swordfish, mackerel) bioaccumulate plastic alongside mercury.</li>
        <li><strong>Wild-caught vs farmed</strong> matters less than people think, both contain measurable microplastic.</li>
        <li><strong>Whole small fish</strong> (anchovies, sardines, ikura roe) include the gut, increasing particle load per gram.</li>
      </ul>
      <p>
        See our parent article on{' '}
        <Link href="/blog/microplastics-in-seafood" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in seafood
        </Link>{' '}
        for the broader picture.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        The black plastic sushi tray problem
      </h2>
      <p>
        The 2024 Toxic-Free Future / <em>Chemosphere</em> study tested 203 black
        plastic consumer products. Sushi trays were among the worst, many
        tested positive for brominated flame retardants from recycled
        electronics waste. The black plastic is in direct contact with raw
        fish, rice, and seaweed for the entire shelf life from supermarket
        prep to your meal (sometimes 6-12 hours or more).
      </p>
      <p>
        The fix: avoid black plastic trays. Restaurants serving on ceramic
        plates eliminate this entirely. Some upscale grocers (Whole Foods, Erewhon)
        use clear PET trays which are not flame-retardant-contaminated but still
        plastic-contact.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Fish-shaped soy sauce packets
      </h2>
      <p>
        Those iconic little fish-shaped soy sauce containers (called
        <em> shoyu-tai</em>) are typically made of polyethylene or, in some
        markets, PLA bioplastic. They sit in direct contact with concentrated
        soy sauce (a salty, somewhat acidic liquid) sometimes for months
        of shelf life. Microplastic and plasticiser release into the soy is
        not well-quantified but likely meaningful. Bring your own glass-bottled
        soy sauce when possible.
      </p>

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Sushi format ranked
      </h2>

      <BlogComparisonTable
        caption="Sushi formats by microplastic exposure"
        columns={[
          { key: 'format', label: 'Format' },
          { key: 'risk', label: 'Relative exposure' },
          { key: 'why', label: 'Why' },
        ]}
        rows={[
          { format: 'Restaurant sushi on ceramic, prepared fresh', risk: 'Lowest', why: 'Minimal plastic contact; fresh prep eliminates extended tray time' },
          { format: 'Restaurant takeout in fiber/paper box', risk: 'Low', why: 'Eliminates tray; still has soy packet' },
          { format: 'Supermarket sushi in clear PET tray', risk: 'Moderate', why: 'PET contact for hours; no e-waste flame retardant issue' },
          { format: 'Supermarket sushi in black plastic tray', risk: 'Higher', why: 'Brominated flame retardants from recycled e-waste' },
          { format: 'Convenience-store sushi (long shelf life)', risk: 'Higher', why: 'Hours-to-days in plastic packaging + lower-grade fish' },
          { format: 'Home-made sushi from filleted fish on glass/ceramic', risk: 'Lowest', why: 'Full control over fish source and packaging' },
        ]}
      />

      <h2 className="font-display text-2xl font-bold text-ink mt-10">
        Practical changes
      </h2>
      <ol className="list-decimal pl-6 space-y-2">
        <li><strong>Eat sushi at the restaurant</strong> rather than takeout when possible.</li>
        <li><strong>Choose smaller fish</strong> (salmon, mackerel, sardines) over large predators (bluefin tuna, swordfish) for both mercury and microplastic reasons.</li>
        <li><strong>Avoid black plastic trays</strong> entirely, ask for ceramic or compostable fibre containers.</li>
        <li><strong>Skip fish-shaped soy sauce packets</strong>, bring your own glass-bottled soy.</li>
        <li><strong>Don&apos;t microwave sushi trays</strong>, heat dramatically accelerates plastic leaching.</li>
        <li><strong>Make sushi at home</strong> with filleted fish from a glass tray and a bamboo rolling mat, full control.</li>
        <li><strong>Limit weekly sushi frequency</strong> if you eat it often, average 5g plastic / week guidance applies to high-frequency consumers.</li>
      </ol>

      <p>
        See related:{' '}
        <Link href="/blog/microplastics-in-seafood" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in seafood
        </Link>,{' '}
        <Link href="/blog/microplastics-in-meat" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in meat
        </Link>, and{' '}
        <Link href="/blog/microplastics-in-fast-food" className="text-[#1E6B47] hover:text-[#155235] underline font-medium">
          microplastics in fast food
        </Link>.
      </p>


      <BlogWhatTheAppChecks
        items={[
          <>Packaging material. PET, HDPE, PP, PS, multi-layer, glass, aluminum.</>,
          <>Container condition from the photo, scratches, dents, fade.</>,
          <>Product category, fresh, packaged, canned, frozen, takeout.</>,
          <>Use-context flags you log, microwave, heat, reuse, time stored.</>,
          <>Cited research behind the 0–100 risk score.</>,
        ]}
      />

      <BlogAppCTA
        headline="Use the app as a grocery-store second opinion"
        sub="Scan the product, check the packaging score, compare alternatives. The app weighs material, condition, brand, and the cited research."
        buttonLabel="Scan groceries in the app"
      />

      <BlogFAQ
        items={[
          {
            question: 'Is sushi high in microplastics?',
            answer:
              'Sushi tends to have a higher microplastic load than equivalent cooked seafood meals because raw fish concentrates particles, and supermarket/takeout sushi sits in plastic trays for hours. Restaurant sushi on ceramic plates is significantly cleaner.',
          },
          {
            question: 'Are black plastic sushi trays dangerous?',
            answer:
              'The 2024 Toxic-Free Future / Chemosphere study found brominated flame retardants in 85% of tested black plastic consumer products including sushi trays. These chemicals come from recycled electronics waste and were never approved for food contact.',
          },
          {
            question: 'Which sushi fish has the most microplastics?',
            answer:
              'Large predator fish like bluefin tuna, swordfish, and mackerel bioaccumulate microplastic alongside mercury. Whole small fish preparations (anchovies, sardines, ikura roe) include the gut, increasing particle load per gram.',
          },
          {
            question: 'Are fish-shaped soy sauce packets safe?',
            answer:
              'These small polyethylene or PLA packets sit in direct contact with concentrated soy sauce for months of shelf life. Microplastic migration is plausible but not well-quantified. Bring your own glass-bottled soy sauce for cleaner exposure.',
          },
          {
            question: 'Should I avoid sushi to reduce microplastic exposure?',
            answer:
              'Not entirely. Switch to restaurant sushi served on ceramic plates with separate soy sauce, choose smaller fish over predators, avoid black plastic trays, and bring your own soy. Sushi from restaurants this way is comparable to other seafood for exposure.',
          },
        ]}
      />

      <BlogSources
        items={[
          {
            authors: 'Liu M, Schreder E, Ezell J, et al.',
            year: 2024,
            title: 'From e-waste to living space: Flame retardants in recycled black plastic consumer products',
            publisher: 'Chemosphere',
            url: 'https://www.sciencedirect.com/science/article/abs/pii/S0045653524023963',
          },
          {
            authors: 'European Food Safety Authority',
            year: 2016,
            title: 'Presence of microplastics and nanoplastics in food, with particular focus on seafood',
            publisher: 'EFSA Journal',
            url: 'https://efsa.onlinelibrary.wiley.com/doi/10.2903/j.efsa.2016.4501',
          },
          {
            authors: 'Smith M, Love DC, Rochman CM, Neff RA',
            year: 2018,
            title: 'Microplastics in Seafood and the Implications for Human Health',
            publisher: 'Current Environmental Health Reports',
            url: 'https://link.springer.com/article/10.1007/s40572-018-0206-z',
          },
          {
            authors: 'IARC Working Group',
            year: 2019,
            title: 'Styrene, Styrene-7,8-oxide, and Quinoline (IARC Monograph 121)',
            publisher: 'International Agency for Research on Cancer',
            url: 'https://publications.iarc.fr/581',
          },
        ]}
      />
    </BlogPostLayout>
  )
}
